import { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { Menu, X, Flame } from "lucide-react";
import { HFLinkButton, Container } from "@/components/ui-hf";
import { useSiteContent } from "@/lib/siteContent";
import { useAktion } from "@/lib/useAktion";
import { useAktionPage } from "@/lib/useAktionPage";
import { useAktionsSeite } from "@/lib/useAktionsSeite";
import { useErleben } from "@/lib/useErleben";
import HeaderBrandLogo from "@/components/HeaderBrandLogo";

const NAV = [
    { href: "#idea", label: "Die Idee" },
    { href: "#immersive", label: "360°" },
    { href: "#use-cases", label: "Einsatz" },
    { href: "#gallery", label: "Beispiele" },
    { href: "#process", label: "Ablauf" },
    { href: "#faq", label: "FAQ" },
    { href: "#about", label: "Über" },
    { href: "#kontakt", label: "Kontakt" },
];

// Route-basierte Nav-Einträge (react-router `<Link>`, kein Hash-Scroll).
// „Erleben" führt zur `/erleben`-QR-Landingpage.
const ROUTE_NAV = [
    { to: "/erleben", label: "Erleben", testKey: "erleben" },
];

export default function HFHeader() {
    const [scrolled, setScrolled] = useState(false);
    const [open, setOpen] = useState(false);
    const location = useLocation();
    const navigate = useNavigate();
    const onHome = location.pathname === "/";
    const dialogRef = useRef<HTMLDivElement | null>(null);
    const firstLinkRef = useRef<HTMLAnchorElement | null>(null);
    const openerRef = useRef<HTMLButtonElement | null>(null);
    const { getObject, sectionEnabled } = useSiteContent();
    // Referenzaktion — Master-Switch aus /admin/referenz-aktion. Wenn `active`
    // (siehe `useAktion.ts`) → wir zeigen einen zusätzlichen Aktion-Button
    // im Header (Desktop + Mobile). Auf `/aktion` selbst nicht sichtbar, um
    // Self-Referenz zu vermeiden.
    const aktion = useAktion();
    const onAktion = location.pathname === "/aktion";
    // AKTION-Header-Button — Sichtbarkeit hängt am SEPARATEN Master-Switch der
    // Landing-Page (`aktion_page.enabled`), NICHT am `aktion.enabled`-Feld:
    //   aktion.enabled      → Referenz-Sektion + Banner auf Startseite (/admin/aktion)
    //   aktion_page.enabled → /aktion-Landing + Header-AKTION-Button (/admin/aktions-seite)
    // Diese Trennung erlaubt es, die Landing über QR-Codes / Ads erreichbar
    // zu halten, während die Referenz-Sektion auf der Startseite ausgeschaltet
    // ist (oder umgekehrt). Solange der CMS-Fetch noch offen ist (`!ready`),
    // rendern wir konservativ (Button sichtbar) — kein Flackern.
    void aktion; // aktuell nicht mehr für den Button relevant, bleibt für zukünftige Nav-Logik
    const aktionPage = useAktionPage();
    const showAktionButton = (!aktionPage.ready || aktionPage.enabled) && !onAktion;
    // Beschriftung des AKTION-Buttons kommt aus dem CMS
    // (`aktion_page.header_button`). Der Hook liefert `DEFAULT_CONFIG` sofort
    // aus dem Cache — die Defaults matchen die vorherigen Hardcode-Werte,
    // sodass Legacy-Deployments ohne dieses Feld visuell unverändert bleiben.
    const aktionSeiteCfg = useAktionsSeite();
    const btnLabel = aktionSeiteCfg.header_button;
    // Erleben-Master-Switch — Nav-Punkt "Erleben" nur zeigen, wenn der Admin
    // die Seite aktiv hat. Solange CMS noch lädt (`ready=false`) blenden wir
    // den Punkt nicht aus (kein Flackern beim ersten Paint).
    const erleben = useErleben();
    const showErleben = !erleben.ready || erleben.enabled;
    const routeNav = showErleben ? ROUTE_NAV : ROUTE_NAV.filter((r) => r.to !== "/erleben");
    // Ein Media-Referenz-Feld (Bild ODER Video). Der Renderer erkennt anhand
    // media_type selbst, wie er darstellt. Verwaltet unter /admin/startseite
    // → Panel "Branding".
    const brandLogo = getObject<{ media_id?: string | null }>("brand_logo", { media_id: null });
    // Header sitzt auf Home vor dem Scroll auf einem dunklen Hero-Overlay →
    // Icon + Text hell, damit der Hamburger sichtbar ist. Sobald gescrollt
    // oder auf Unterseiten → dunkler Ink-Text.
    const heroDark = onHome && !scrolled;

    /**
     * Nav-Items nach Sektion-Enabled-Status filtern.
     *
     * Wenn der User im Admin z. B. „home.gallery" deaktiviert hat, würde
     * ein Klick auf „Beispiele" ins Leere laufen (Section nicht im DOM).
     * Statt broken Link → Nav-Eintrag ausblenden. Off-Home werden alle
     * Anchors gezeigt (Klick navigiert dann zu Home + Anchor; falls die
     * Ziel-Section dort deaktiviert ist, scrollt ScrollToTop bis zu 3 s und
     * gibt danach still auf — kein Crash).
     *
     * Mapping Anchor → CMS-Section-Key:
     */
    const anchorToSection: Record<string, string> = {
        "#idea": "home.idea",
        "#immersive": "home.lenses",
        "#use-cases": "home.use_cases",
        "#gallery": "home.gallery",
        "#process": "home.process",
        "#faq": "home.faq",
        "#about": "home.about",
        "#kontakt": "home.contact",
    };
    const visibleNav = NAV.filter((item) => {
        if (!onHome) return true; // off-home immer zeigen
        const secKey = anchorToSection[item.href];
        if (!secKey) return true; // unbekannt → sichtbar lassen
        return sectionEnabled(secKey);
    });

    useEffect(() => {
        const onScroll = () => setScrolled(window.scrollY > 40);
        onScroll();
        window.addEventListener("scroll", onScroll, { passive: true });
        return () => window.removeEventListener("scroll", onScroll);
    }, []);

    useEffect(() => {
        setOpen(false);
    }, [location.pathname]);

    // Body-Scroll-Lock, ESC, Focus-Trap, Autofocus – nur wenn geöffnet.
    useEffect(() => {
        if (!open) return;
        const prevOverflow = document.body.style.overflow;
        document.body.style.overflow = "hidden";
        firstLinkRef.current?.focus();
        const onKey = (e: KeyboardEvent) => {
            if (e.key === "Escape") {
                setOpen(false);
                return;
            }
            if (e.key !== "Tab") return;
            const dlg = dialogRef.current;
            if (!dlg) return;
            const focusables = dlg.querySelectorAll<HTMLElement>(
                'a[href], button:not([disabled]), [tabindex]:not([tabindex="-1"])',
            );
            if (focusables.length === 0) return;
            const first = focusables[0];
            const last = focusables[focusables.length - 1];
            if (e.shiftKey && document.activeElement === first) {
                e.preventDefault();
                last.focus();
            } else if (!e.shiftKey && document.activeElement === last) {
                e.preventDefault();
                first.focus();
            }
        };
        document.addEventListener("keydown", onKey);
        return () => {
            document.body.style.overflow = prevOverflow;
            document.removeEventListener("keydown", onKey);
            openerRef.current?.focus();
        };
    }, [open]);

    /**
     * Einheitlicher Handler für Anchor-Nav (Desktop + Mobile).
     *
     * - Same-Route (Home + `#anchor`): smooth `scrollIntoView` zum Element.
     *   Menu schließt VOR dem Scroll — dann rAF, dann Scroll, damit
     *   Portal-Unmount und Overflow-Reset abgeschlossen sind (verhindert
     *   den Mobile-Crash beim Klick).
     * - Cross-Route (nicht auf Home): `navigate("/", { state: { anchor }})`
     *   → SPA-Route-Change ohne Hard-Reload; ScrollToTop-Component
     *   liest den State und scrollt nach Home-Load.
     *
     * `<a href="…">` bleibt als Progressive-Enhancement erhalten
     * (Right-Click „In neuem Tab öffnen" funktioniert weiter).
     */
    const goTo = (href: string) => (e: React.MouseEvent) => {
        if (!href.startsWith("#")) return;
        e.preventDefault();
        const anchor = href.slice(1);
        // 1) Menu zuerst schließen (State-Update). Overflow-Reset läuft im
        //    useEffect-Cleanup.
        setOpen(false);
        /** Robuster Scroll: wenn der Zielabschnitt Lazy-Load-Content hat und
         *  zum Klick-Zeitpunkt noch keine finale Höhe erreicht ist, würde ein
         *  einzelner `scrollIntoView` an einer zu niedrigen Y-Position
         *  landen. Wir poll-verifizieren bis zu 1 s und re-scrollen wenn der
         *  Container-Top nicht innerhalb ±60 px vom Viewport-Top ist.
         */
        const scrollAndVerify = () => {
            const el = document.getElementById(anchor);
            if (!el) return false;
            el.scrollIntoView({ behavior: "smooth", block: "start" });
            try {
                window.history.replaceState(
                    null,
                    "",
                    `${window.location.pathname}#${anchor}`,
                );
            } catch { /* ignore */ }
            return true;
        };
        if (onHome) {
            // Same-Route: 2 rAFs damit Portal-Unmount + Overflow-Reset abgeschlossen sind.
            requestAnimationFrame(() => requestAnimationFrame(() => {
                if (!scrollAndVerify()) {
                    // Element noch nicht gemounted → cross-route Fallback.
                    navigate("/", { state: { anchor } });
                    return;
                }
                // Re-Verify nach 250 ms (Lazy-Load-Sections können höher werden)
                window.setTimeout(() => {
                    const el = document.getElementById(anchor);
                    if (!el) return;
                    const rect = el.getBoundingClientRect();
                    if (Math.abs(rect.top) > 60) {
                        el.scrollIntoView({ behavior: "smooth", block: "start" });
                    }
                }, 250);
                window.setTimeout(() => {
                    const el = document.getElementById(anchor);
                    if (!el) return;
                    const rect = el.getBoundingClientRect();
                    if (Math.abs(rect.top) > 60) {
                        el.scrollIntoView({ behavior: "smooth", block: "start" });
                    }
                }, 700);
            }));
        } else {
            // Cross-Route: sofort routen — ScrollToTop übernimmt den Scroll
            navigate("/", { state: { anchor } });
        }
    };

    return (
        <>
            <a
                href="#main-content"
                className="hf-skip-link"
                data-testid="site-skip-link"
            >
                Zum Inhalt springen
            </a>
            <header
                data-testid="site-header"
                className="sticky top-0 z-40 border-b transition-all"
            style={{
                background: "hsl(var(--hf-bg))",
                backdropFilter: "blur(12px)",
                WebkitBackdropFilter: "blur(12px)",
                borderColor: "rgba(167, 129, 80, 0.20)",
            }}
        >
            <Container
                className={`flex items-center justify-between transition-all ${scrolled ? "py-0.5 md:py-1" : "py-1 md:py-1.5"}`}
            >
                <Link
                    to="/"
                    data-testid="site-header-logo"
                    className="flex items-center"
                >
                    <HeaderBrandLogo
                        mediaId={brandLogo.media_id}
                        className="shrink-0 h-12 md:h-24 lg:h-28 xl:h-36 w-[142px] md:w-[284px] lg:w-[332px] xl:w-[426px] object-contain"
                    />
                </Link>

                <nav
                    className="hidden xl:flex items-center gap-x-5 flex-nowrap whitespace-nowrap hf-nav-link-list"
                    aria-label="Hauptnavigation"
                    data-testid="site-nav"
                >
                    {visibleNav.map((n) => (
                        <a
                            key={n.href}
                            href={onHome ? n.href : `/${n.href}`}
                            onClick={goTo(n.href)}
                            className="hf-nav-link"
                            data-testid={`site-nav-${n.href.slice(1)}`}
                        >
                            {n.label}
                        </a>
                    ))}
                    {routeNav.map((r) => (
                        <Link
                            key={r.to}
                            to={r.to}
                            className={`hf-nav-link ${
                                location.pathname === r.to ? "hf-nav-link-active" : ""
                            }`}
                            data-testid={`site-nav-${r.testKey}`}
                        >
                            {r.label}
                        </Link>
                    ))}
                    <Link
                        to="/sylt"
                        className={`hf-nav-link inline-flex items-center gap-1 ${
                            location.pathname === "/sylt" ? "hf-nav-link-active" : ""
                        }`}
                        data-testid="site-nav-sylt"
                    >
                        <span
                            className="text-[9px] tracking-[0.16em] uppercase px-1.5 py-0.5 rounded-sm"
                            style={{
                                background: "hsl(var(--hf-gold-soft))",
                                color: "hsl(var(--hf-ink))",
                                textShadow: "none",
                            }}
                        >
                            Insel
                        </span>
                        Sylt
                    </Link>
                </nav>

                <div className="flex items-center gap-2 md:gap-3">
                    {showAktionButton && (
                        <Link
                            to="/aktion"
                            data-testid="site-header-aktion-cta"
                            className="hidden md:inline-flex items-center gap-1.5 md:gap-2 px-2 md:px-2.5 xl:px-3.5 py-1.5 md:py-2 rounded-sm transition-all hover:brightness-110 focus:outline-none focus-visible:ring-2 whitespace-nowrap"
                            style={{
                                background: "linear-gradient(180deg, #E23A24 0%, #B92214 100%)",
                                color: "#FFFDF9",
                                border: "1.5px solid #D4A855",
                                boxShadow: "0 4px 14px -4px rgba(226, 58, 36, 0.45), inset 0 0 0 1px rgba(255,255,255,0.08)",
                            }}
                            aria-label={`Zur Referenz-Aktion — ${btnLabel.main_text}`}
                        >
                            <span
                                aria-hidden
                                className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-[2px] font-anton uppercase text-[9px] xl:text-[10px] tracking-[0.14em]"
                                style={{
                                    background: "linear-gradient(180deg, #F1D48A 0%, #D4A855 100%)",
                                    color: "#0F1830",
                                    letterSpacing: "0.12em",
                                    lineHeight: 1,
                                }}
                            >
                                {btnLabel.show_icon && <Flame size={10} strokeWidth={2.6} />}
                                {btnLabel.chip_text}
                            </span>
                            <span
                                className="font-anton uppercase text-[10px] xl:text-[12px] tracking-[0.10em] xl:tracking-[0.14em]"
                                style={{ letterSpacing: "0.10em" }}
                            >
                                <span className="hidden xl:inline">{btnLabel.main_text} </span>
                                <span aria-hidden>→</span>
                            </span>
                        </Link>
                    )}
                    <HFLinkButton
                        href={onHome ? "#kontakt" : "/#kontakt"}
                        onClick={goTo("#kontakt")}
                        variant="primary"
                        className="!py-1.5 !px-3 md:!py-2 md:!px-4 !text-[10px] md:!text-[13px] !tracking-[0.10em] md:!tracking-[0.16em] !gap-1.5 !leading-[1.05] md:!leading-normal"
                        data-testid="site-header-cta"
                        trailing={<span aria-hidden>→</span>}
                    >
                        <span className="inline-flex flex-col md:flex-row md:gap-1.5 items-center leading-[1.05] md:leading-none">
                            <span>Unverbindlich</span>
                            <span>anfragen</span>
                        </span>
                    </HFLinkButton>
                    <button
                        ref={openerRef}
                        type="button"
                        onClick={() => setOpen(true)}
                        className="xl:hidden inline-flex items-center justify-center w-11 h-11 rounded-sm border transition-colors"
                        style={{
                            borderColor: heroDark
                                ? "hsl(var(--hf-bg) / 0.5)"
                                : "hsl(var(--hf-line))",
                            color: heroDark
                                ? "hsl(var(--hf-bg))"
                                : "hsl(var(--hf-ink))",
                            background: heroDark
                                ? "hsl(var(--hf-ink) / 0.35)"
                                : "transparent",
                        }}
                        aria-label="Menü öffnen"
                        aria-expanded={open}
                        aria-controls="site-mobile-menu"
                        data-testid="site-header-menu-open"
                    >
                        <Menu size={22} />
                    </button>
                </div>
            </Container>
            </header>

            {/* Mobile off-canvas – Full-Screen Editorial (Portal auf body,
                damit weder sticky-Header noch backdrop-filter ein containing
                block erzeugen kann) */}
            {open && createPortal(
                <div
                    id="site-mobile-menu"
                    ref={dialogRef}
                    role="dialog"
                    aria-modal="true"
                    aria-label="Mobile Navigation"
                    className="xl:hidden fixed inset-0 z-[100] flex flex-col"
                    style={{
                        background: "hsl(var(--hf-ink))",
                        color: "hsl(var(--hf-bg))",
                    }}
                    data-testid="site-mobile-menu"
                    onClick={(e) => e.stopPropagation()}
                >
                    {/* Header oben: Logo + X */}
                    <div
                        className="flex items-center justify-between px-6 py-5 border-b"
                        style={{ borderColor: "hsl(var(--hf-bg) / 0.14)" }}
                    >
                        <Link
                            to="/"
                            onClick={() => setOpen(false)}
                            className="flex items-center gap-3"
                            data-testid="site-mobile-menu-logo"
                        >
                            <span
                                className="inline-flex items-center justify-center w-10 h-10 font-display text-lg"
                                style={{
                                    background: "hsl(var(--hf-gold))",
                                    color: "hsl(var(--hf-ink))",
                                    letterSpacing: "-0.02em",
                                }}
                            >
                                H
                            </span>
                            <span className="flex flex-col leading-tight">
                                <span
                                    className="font-display text-lg"
                                    style={{ color: "hsl(var(--hf-bg))" }}
                                >
                                    HypeFly
                                </span>
                                <span
                                    className="hf-eyebrow"
                                    style={{
                                        fontSize: "10px",
                                        color: "hsl(var(--hf-gold-soft))",
                                    }}
                                >
                                    Premium 360°
                                </span>
                            </span>
                        </Link>
                        <button
                            type="button"
                            onClick={() => setOpen(false)}
                            className="w-11 h-11 inline-flex items-center justify-center rounded-sm border"
                            style={{
                                borderColor: "hsl(var(--hf-bg) / 0.28)",
                                color: "hsl(var(--hf-bg))",
                            }}
                            aria-label="Menü schließen"
                            data-testid="site-header-menu-close"
                        >
                            <X size={22} />
                        </button>
                    </div>

                    {/* Nav */}
                    <nav
                        className="flex-1 overflow-y-auto px-6 pt-4 pb-6"
                        aria-label="Mobile Hauptnavigation"
                    >
                        {showAktionButton && (
                            <Link
                                to="/aktion"
                                onClick={() => setOpen(false)}
                                data-testid="site-mobile-nav-aktion"
                                className="flex items-center justify-between gap-3 mb-4 px-5 py-4 rounded-sm transition-all"
                                style={{
                                    background: "linear-gradient(180deg, #E23A24 0%, #B92214 100%)",
                                    color: "#FFFDF9",
                                    border: "1.5px solid #D4A855",
                                    boxShadow: "0 6px 18px -6px rgba(226,58,36,0.55)",
                                }}
                            >
                                <div className="flex items-center gap-3">
                                    <span
                                        aria-hidden
                                        className="inline-flex items-center gap-1 px-2 py-1 rounded-[3px] font-anton uppercase text-[11px] tracking-[0.16em]"
                                        style={{
                                            background: "linear-gradient(180deg, #F1D48A 0%, #D4A855 100%)",
                                            color: "#0F1830",
                                            lineHeight: 1,
                                        }}
                                    >
                                        {btnLabel.show_icon && <Flame size={12} strokeWidth={2.6} />}
                                        {btnLabel.chip_text}
                                    </span>
                                    <span className="font-anton uppercase text-base tracking-[0.10em]">
                                        {btnLabel.main_text}
                                    </span>
                                </div>
                                <span aria-hidden className="font-anton text-xl leading-none">→</span>
                            </Link>
                        )}
                        <span
                            className="hf-eyebrow block mb-3"
                            style={{ color: "hsl(var(--hf-gold-soft))" }}
                        >
                            Navigation
                        </span>
                        <ul className="flex flex-col">
                            {visibleNav.map((n, idx) => (
                                <li key={n.href}>
                                    <a
                                        ref={idx === 0 ? firstLinkRef : undefined}
                                        href={onHome ? n.href : `/${n.href}`}
                                        onClick={goTo(n.href)}
                                        className="group relative flex items-center py-4 pl-4 pr-2 border-b text-2xl hf-nav-link-inverse focus-visible:outline-none"
                                        style={{
                                            borderColor:
                                                "hsl(var(--hf-bg) / 0.10)",
                                        }}
                                        data-testid={`site-mobile-nav-${n.href.slice(1)}`}
                                    >
                                        <span
                                            aria-hidden
                                            className="absolute left-0 top-1/2 -translate-y-1/2 h-6 w-[2px] opacity-0 group-hover:opacity-100 group-focus-visible:opacity-100 transition-opacity"
                                            style={{
                                                background:
                                                    "hsl(var(--hf-gold))",
                                            }}
                                        />
                                        {n.label}
                                    </a>
                                </li>
                            ))}
                            {routeNav.map((r) => (
                                <li key={r.to}>
                                    <Link
                                        to={r.to}
                                        onClick={() => setOpen(false)}
                                        className="group relative flex items-center py-4 pl-4 pr-2 border-b text-2xl hf-nav-link-inverse"
                                        style={{
                                            borderColor: "hsl(var(--hf-bg) / 0.10)",
                                        }}
                                        data-testid={`site-mobile-nav-${r.testKey}`}
                                    >
                                        <span
                                            aria-hidden
                                            className="absolute left-0 top-1/2 -translate-y-1/2 h-6 w-[2px] opacity-0 group-hover:opacity-100 transition-opacity"
                                            style={{
                                                background: "hsl(var(--hf-gold))",
                                            }}
                                        />
                                        {r.label}
                                    </Link>
                                </li>
                            ))}
                            <li>
                                <Link
                                    to="/sylt"
                                    onClick={() => setOpen(false)}
                                    className="group relative flex items-center gap-3 py-4 pl-4 pr-2 border-b font-display text-xl tracking-wide transition-colors hover:text-hf-gold-soft"
                                    style={{
                                        borderColor:
                                            "hsl(var(--hf-bg) / 0.10)",
                                        color: "hsl(var(--hf-bg))",
                                    }}
                                    data-testid="site-mobile-nav-sylt"
                                >
                                    <span
                                        aria-hidden
                                        className="absolute left-0 top-1/2 -translate-y-1/2 h-6 w-[2px] opacity-0 group-hover:opacity-100 transition-opacity"
                                        style={{
                                            background: "hsl(var(--hf-gold))",
                                        }}
                                    />
                                    <span
                                        className="text-[10px] tracking-[0.18em] uppercase px-1.5 py-0.5 rounded-sm"
                                        style={{
                                            background:
                                                "hsl(var(--hf-gold))",
                                            color: "hsl(var(--hf-ink))",
                                        }}
                                    >
                                        Insel
                                    </span>
                                    Sylt
                                </Link>
                            </li>
                        </ul>
                    </nav>

                    {/* CTA unten */}
                    <div
                        className="px-6 py-6 border-t"
                        style={{
                            borderColor: "hsl(var(--hf-bg) / 0.14)",
                            background: "hsl(var(--hf-ink))",
                        }}
                    >
                        <HFLinkButton
                            href={onHome ? "#kontakt" : "/#kontakt"}
                            onClick={goTo("#kontakt")}
                            variant="primary"
                            className="w-full justify-center"
                            data-testid="site-mobile-menu-cta"
                            trailing={<span aria-hidden>→</span>}
                        >
                            Unverbindlich anfragen
                        </HFLinkButton>
                        <p
                            className="mt-3 text-xs text-center"
                            style={{ color: "hsl(var(--hf-bg) / 0.55)" }}
                        >
                            Antwort innerhalb von 24 Stunden.
                        </p>
                    </div>
                </div>,
                document.body,
            )}
        </>
    );
}
